import { useEffect, useState } from 'react';
import { Modal, Button, Image, Upload, message } from 'antd';
import config from '@/config';
import { fileApi } from '@services/Conference';
import './HallPlanImageModal.less';

const uploadOption = { headers: { 'Content-Type': 'multipart/form-data' } };

/** 会场规划图上传限制：最多 5 张，单张最大 5MB，仅 jpg/png */
const MAX_IMAGE_COUNT = 5;
const MAX_IMAGE_SIZE_MB = 5;

/** 补全图片访问地址（相对路径拼接 OSS 前缀） */
function resolveImageUrl(url) {
    if (!url) return url;
    if (url.includes('http')) return url;
    return config.OSS_PREFIX + url;
}

/** 图片格式与大小校验（JPG/PNG，不超过 5MB） */
function checkImageFile(file) {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
    if (!isJpgOrPng) {
        message.error('只能上传 jpg/png 格式');
        return false;
    }
    const isLt5M = file.size / 1024 / 1024 <= MAX_IMAGE_SIZE_MB;
    if (!isLt5M) {
        message.error(`图片大小不能超过 ${MAX_IMAGE_SIZE_MB}MB!`);
        return false;
    }
    return true;
}

/**
 * 会场规划图弹窗（管理 / 查看两种模式）
 * @param {boolean} open 是否打开
 * @param {'manage'|'view'} mode 模式：manage-管理（可上传/删除/保存），view-查看（隐藏上传与底部按钮）
 * @param {string[]} images 初始图片地址数组
 * @param {function} onCancel 关闭回调
 * @param {function} onSave 保存回调，参数为最新图片地址数组
 */
export default function HallPlanImageModal({ open, mode = 'manage', images: initialImages = [], onCancel, onSave }) {
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const isView = mode === 'view';

    // 每次打开时同步初始图片
    useEffect(() => {
        if (open) {
            setImages(Array.isArray(initialImages) ? initialImages : []);
        }
    }, [open, initialImages]);

    const handleUpload = info => {
        const formData = new FormData();
        formData.append('file', info.file);
        formData.append('type', 14);
        setUploading(true);
        fileApi(formData, uploadOption)
            .then(res => {
                setUploading(false);
                if (!res) {
                    message.error('上传文件失败！');
                    return;
                }
                if (res.status_code === 200) {
                    setImages(prev => [...prev, resolveImageUrl(res.data)]);
                }
            })
            .catch(() => {
                setUploading(false);
                message.error('上传文件失败！');
            });
    };

    const handleBeforeUpload = file => {
        if (images.length >= MAX_IMAGE_COUNT) {
            message.error(`最多上传 ${MAX_IMAGE_COUNT} 张图片`);
            return Upload.LIST_IGNORE;
        }
        return checkImageFile(file);
    };

    const removeImage = index => {
        setImages(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <Modal
            title={isView ? '查看会场规划图' : '管理会场规划图'}
            open={open}
            onCancel={onCancel}
            width={720}
            centered
            className="hall-plan-image-modal"
            footer={
                isView
                    ? null
                    : [
                          <Button key="cancel" onClick={onCancel}>
                              取消
                          </Button>,
                          <Button key="save" type="primary" onClick={() => onSave(images)}>
                              保存
                          </Button>,
                      ]
            }
        >
            <div className="hall-plan-images">
                {images.map((url, index) => (
                    <div className="hall-plan-image-item" key={url}>
                        <Image src={url} width={160} height={110} style={{ objectFit: 'cover' }} />
                        {!isView && <i className="bi bi-x-circle-fill hall-plan-image-close" onClick={() => removeImage(index)} aria-hidden="true" />}
                    </div>
                ))}
                {!isView && images.length < MAX_IMAGE_COUNT && (
                    <Upload beforeUpload={handleBeforeUpload} fileList={[]} customRequest={handleUpload} accept="image/jpeg, image/png, image/jpg" showUploadList={false}>
                        <div className={`hall-plan-upload-trigger${uploading ? ' is-uploading' : ''}`}>
                            <i className={uploading ? 'bi bi-arrow-repeat is-loading' : 'bi bi-plus-lg'} aria-hidden="true" />
                            <span>{uploading ? '上传中...' : '上传图片'}</span>
                            <span className="hall-plan-upload-tip">
                                最多 {MAX_IMAGE_COUNT} 张，单张 ≤ {MAX_IMAGE_SIZE_MB}MB，仅 jpg/png
                            </span>
                        </div>
                    </Upload>
                )}
                {isView && images.length === 0 && <span className="hall-plan-empty">暂未上传会场规划图</span>}
            </div>
        </Modal>
    );
}
